// Hardware-accurate interactive controller component (Signal Arcade Studios —
// Yuki Nakamura, Technical Art). Each coming-soon game card gets an SVG
// controller that matches the control style of the game and animates on hover
// or keyboard focus, previewing how the title will play once it ships. The
// same factories drop into the live game stage when originals go playable.
//
// Desktop-only: the CSS hides these under 760px, where games take touch input
// directly on the canvas.
//
// Security: every node is built with createElementNS and static, hard-coded
// attribute values — no innerHTML and no interpolation of external data — so
// this introduces no markup-injection surface (matches the codebase's
// textContent-only discipline).

import type { EngineFamily } from "../shared/catalog";
import "./controller.css";

// The three hardware archetypes we render. Cloudflare originals map by engine
// family; console classics can request a specific archetype by name.
export type ControllerKind = "arcade" | "dpad" | "paddle";

const SVGNS = "http://www.w3.org/2000/svg";

// Map an original's engine family to the hardware that best matches its
// control scheme.
//   combat  -> arcade joystick + fire button (FPS / rail shooter)
//   motion  -> paddle / dial (racing, flight, steering)
//   grid    -> D-pad + action button (puzzle, adventure, tower defense)
export function controllerForFamily(family: EngineFamily): ControllerKind {
  switch (family) {
    case "combat":
      return "arcade";
    case "motion":
      return "paddle";
    case "grid":
    default:
      return "dpad";
  }
}

const HARDWARE_LABEL: Record<ControllerKind, string> = {
  arcade: "Arcade joystick",
  dpad: "D-pad + action",
  paddle: "Paddle dial",
};

export function hardwareLabel(kind: ControllerKind): string {
  return HARDWARE_LABEL[kind];
}

// --- small typed helpers over createElementNS -------------------------------

function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string>,
  parent?: SVGElement,
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVGNS, tag);
  for (const [key, value] of Object.entries(attrs)) {
    node.setAttribute(key, value);
  }
  if (parent) parent.appendChild(node);
  return node;
}

function baseSvg(viewBox: string, label: string): SVGSVGElement {
  const svg = document.createElementNS(SVGNS, "svg");
  svg.setAttribute("class", "ctrl-pad");
  svg.setAttribute("viewBox", viewBox);
  svg.setAttribute("role", "img");
  svg.setAttribute("aria-label", label);
  return svg;
}

// --- arcade joystick + fire button ------------------------------------------

function buildArcade(): SVGSVGElement {
  const svg = baseSvg("0 0 360 200", "Arcade joystick and fire button");
  const defs = svgEl("defs", {}, svg);

  const ballG = svgEl("radialGradient", { id: "ctrlBallG", cx: "35%", cy: "30%" }, defs);
  svgEl("stop", { offset: "0%", "stop-color": "#ff6a6a" }, ballG);
  svgEl("stop", { offset: "100%", "stop-color": "#b81d1d" }, ballG);

  const panelG = svgEl(
    "linearGradient",
    { id: "ctrlPanelG", x1: "0", y1: "0", x2: "0", y2: "1" },
    defs,
  );
  svgEl("stop", { offset: "0%", "stop-color": "#243444" }, panelG);
  svgEl("stop", { offset: "100%", "stop-color": "#111c26" }, panelG);

  const btnG = svgEl("radialGradient", { id: "ctrlBtnG", cx: "35%", cy: "30%" }, defs);
  svgEl("stop", { offset: "0%", "stop-color": "#ffd76a" }, btnG);
  svgEl("stop", { offset: "100%", "stop-color": "#e0961a" }, btnG);

  // panel body (production can swap this rect for an <image href> WebP photo)
  svgEl(
    "rect",
    {
      x: "6",
      y: "14",
      width: "348",
      height: "172",
      rx: "20",
      fill: "url(#ctrlPanelG)",
      stroke: "#000",
      "stroke-opacity": ".5",
    },
    svg,
  );
  // joystick base
  svgEl("ellipse", { cx: "110", cy: "130", rx: "52", ry: "20", fill: "#0a121a" }, svg);
  svgEl("ellipse", { cx: "110", cy: "126", rx: "40", ry: "15", fill: "#1b2a38" }, svg);
  // shaft + ball (this group tilts on animation)
  const stick = svgEl("g", { class: "ctrl-stick" }, svg);
  svgEl("rect", { x: "104", y: "70", width: "12", height: "56", rx: "6", fill: "#c9d4dc" }, stick);
  svgEl(
    "circle",
    { cx: "110", cy: "64", r: "22", fill: "url(#ctrlBallG)", stroke: "#7a0f0f", "stroke-width": "1.5" },
    stick,
  );
  svgEl("ellipse", { cx: "103", cy: "57", rx: "7", ry: "4", fill: "#fff", "fill-opacity": ".5" }, stick);
  // fire button
  svgEl("ellipse", { cx: "270", cy: "120", rx: "46", ry: "44", fill: "#0a121a" }, svg);
  svgEl(
    "circle",
    {
      class: "ctrl-fire",
      cx: "270",
      cy: "116",
      r: "38",
      fill: "url(#ctrlBtnG)",
      stroke: "#8a5a10",
      "stroke-width": "2",
    },
    svg,
  );
  const label = svgEl(
    "text",
    {
      x: "270",
      y: "122",
      "text-anchor": "middle",
      "font-size": "15",
      "font-weight": "800",
      fill: "#5a3c08",
    },
    svg,
  );
  label.textContent = "FIRE";
  return svg;
}

// --- NES-style D-pad + action button ----------------------------------------

function buildDpad(): SVGSVGElement {
  const svg = baseSvg("0 0 320 150", "Directional pad and action buttons");
  const defs = svgEl("defs", {}, svg);
  const body = svgEl(
    "linearGradient",
    { id: "ctrlNesBody", x1: "0", y1: "0", x2: "0", y2: "1" },
    defs,
  );
  svgEl("stop", { offset: "0", "stop-color": "#c9ccce" }, body);
  svgEl("stop", { offset: "1", "stop-color": "#9a9ea1" }, body);

  svgEl(
    "rect",
    { x: "8", y: "20", width: "304", height: "110", rx: "10", fill: "url(#ctrlNesBody)", stroke: "#5a5d60" },
    svg,
  );
  svgEl("rect", { x: "20", y: "30", width: "280", height: "26", rx: "4", fill: "#7d1f1f" }, svg);

  const dOff = "#2a3742";
  // D-pad cross
  svgEl("rect", { x: "26", y: "86", width: "26", height: "26", fill: "#1b1b1b" }, svg);
  svgEl("rect", { x: "78", y: "86", width: "26", height: "26", fill: "#1b1b1b" }, svg);
  svgEl("rect", { class: "ctrl-dpad-up", x: "52", y: "60", width: "26", height: "26", fill: dOff }, svg);
  svgEl("rect", { class: "ctrl-dpad-down", x: "52", y: "112", width: "26", height: "26", fill: dOff }, svg);
  svgEl("rect", { class: "ctrl-dpad-left", x: "26", y: "86", width: "26", height: "26", fill: dOff }, svg);
  svgEl("rect", { class: "ctrl-dpad-right", x: "78", y: "86", width: "26", height: "26", fill: dOff }, svg);
  svgEl("rect", { x: "52", y: "86", width: "26", height: "26", fill: "#1b1b1b" }, svg);
  // select / start
  svgEl("rect", { x: "130", y: "96", width: "26", height: "9", rx: "4", fill: "#2a2a2a" }, svg);
  svgEl("rect", { x: "164", y: "96", width: "26", height: "9", rx: "4", fill: "#2a2a2a" }, svg);
  // B / A action buttons
  svgEl("circle", { class: "ctrl-b", cx: "228", cy: "98", r: "16", fill: "#b81d1d", stroke: "#7a0f0f" }, svg);
  svgEl("circle", { class: "ctrl-a", cx: "272", cy: "98", r: "16", fill: "#b81d1d", stroke: "#7a0f0f" }, svg);
  const bText = svgEl(
    "text",
    { x: "228", y: "132", "text-anchor": "middle", "font-size": "11", fill: "#333", "font-weight": "800" },
    svg,
  );
  bText.textContent = "B";
  const aText = svgEl(
    "text",
    { x: "272", y: "132", "text-anchor": "middle", "font-size": "11", fill: "#333", "font-weight": "800" },
    svg,
  );
  aText.textContent = "A";
  return svg;
}

// --- Atari-style paddle dial ------------------------------------------------

function buildPaddle(): SVGSVGElement {
  const svg = baseSvg("0 0 200 150", "Paddle dial and fire button");
  svgEl("rect", { x: "40", y: "40", width: "120", height: "80", rx: "12", fill: "#2b2b2b", stroke: "#000" }, svg);
  svgEl("circle", { cx: "100", cy: "80", r: "34", fill: "#141414", stroke: "#000" }, svg);
  const knob = svgEl("g", { class: "ctrl-knob" }, svg);
  svgEl("circle", { cx: "100", cy: "80", r: "30", fill: "#3a3a3a", stroke: "#111" }, knob);
  svgEl("rect", { x: "97", y: "52", width: "6", height: "26", rx: "3", fill: "#e75bc4" }, knob);
  svgEl("rect", { class: "ctrl-fire", x: "70", y: "120", width: "60", height: "14", rx: "4", fill: "#b81d1d" }, svg);
  const label = svgEl(
    "text",
    { x: "100", y: "131", "text-anchor": "middle", "font-size": "9", fill: "#fff", "font-weight": "800" },
    svg,
  );
  label.textContent = "FIRE";
  return svg;
}

const BUILDERS: Record<ControllerKind, () => SVGSVGElement> = {
  arcade: buildArcade,
  dpad: buildDpad,
  paddle: buildPaddle,
};

// Build a controller element for a card. Returns a figure containing the SVG
// and a hardware caption. Animation is driven purely by CSS on :hover / focus
// of the enclosing card, so there are no runtime listeners or timers to leak.
export function createController(kind: ControllerKind): HTMLElement {
  const figure = document.createElement("figure");
  figure.className = `card-controller card-controller--${kind}`;
  figure.setAttribute("aria-hidden", "true");
  figure.appendChild(BUILDERS[kind]());
  const caption = document.createElement("figcaption");
  caption.className = "card-controller__label";
  caption.textContent = hardwareLabel(kind);
  figure.appendChild(caption);
  return figure;
}
